/**
 * LEGACY_DECOMMISSIONED (May 22, 2026)
 * Part of the legacy upload subsystem. Frozen. Do not extend.
 * Replacement: separate Video Analysis Engine (see VAE_CLEAN_ROOM_ARCHITECTURE.md).
 *
 * videoCompressor — Smart Video Optimisation (Feb 7 2026 P0).
 *
 * Client-side video compression via ffmpeg.wasm. Designed as a
 * STRICTLY OPTIONAL pre-flight on top of chunkedUpload — if anything
 * goes wrong (capability missing, decode error, timeout, user
 * cancel), the caller falls back to uploading the original file.
 * The chunked upload pipeline is never touched.
 *
 * Owner directive (verbatim):
 *   "Skip compression. Upload original file normally. Do not crash.
 *    Do not block the user. Do not make compression mandatory if
 *    unsupported."
 *
 * Targets:
 *   - Format:      MP4 / H.264
 *   - Resolution:  ≤ 1080p (downscale only; never upscale)
 *   - FPS:         ≤ 30
 *   - Quality:     CRF 23 (good quality, 50-75% smaller)
 *   - Audio:       AAC 96k mono (low bitrate, audible enough for review)
 *
 * Threshold gates (both must pass to attempt compression):
 *   - Size between 100 MB and 800 MB. Smaller doesn't benefit;
 *     larger takes too long to compress on a phone.
 *   - File MIME / extension is mp4 / mov / m4v / webm.
 *
 * Feature flag (default OFF):
 *   - localStorage["lr_enable_video_compression"] === "true"
 *   - or REACT_APP_ENABLE_VIDEO_COMPRESSION === "true"
 */

const COMPRESSION_MIN_BYTES = 100 * 1024 * 1024;   // 100 MB
const COMPRESSION_MAX_BYTES = 800 * 1024 * 1024;   // 800 MB
const COMPRESSIBLE_EXTS = new Set(["mp4", "mov", "m4v", "webm"]);

// ffmpeg core hosted on jsDelivr (matches @ffmpeg/ffmpeg v0.12 API).
// Single-threaded build — works even without cross-origin isolation
// (no SharedArrayBuffer required). Slower but universally compatible.
const FFMPEG_CORE_URL =
  "https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.10/dist/umd/ffmpeg-core.js";
const FFMPEG_WASM_URL =
  "https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.10/dist/umd/ffmpeg-core.wasm";


/**
 * Returns true if the user has explicitly opted in to compression.
 * Default OFF — the redeploy ships exactly the prior upload UX
 * unless someone flips this flag.
 */
export function isCompressionFeatureEnabled() {
  // localStorage override always wins (developer / opt-in beta).
  try {
    if (
      typeof window !== "undefined"
      && window.localStorage?.getItem("lr_enable_video_compression") === "true"
    ) {
      return true;
    }
  } catch {
    // localStorage may be blocked (incognito, privacy); ignore.
  }
  // Build-time env flag.
  if (process.env.REACT_APP_ENABLE_VIDEO_COMPRESSION === "true") return true;
  return false;
}


/**
 * Capability check — runs synchronously, never throws.
 * Returns one of:
 *   { ok: true }
 *   { ok: false, reason: "no-wasm" | "no-worker" | "no-window" | ... }
 */
export function detectCompressionSupport() {
  if (typeof window === "undefined") return { ok: false, reason: "no-window" };
  if (typeof WebAssembly === "undefined") return { ok: false, reason: "no-wasm" };
  if (typeof Worker === "undefined") return { ok: false, reason: "no-worker" };
  // Memory check — ffmpeg.wasm in single-threaded mode uses ~512 MB
  // peak. If the device hint says < 4 GB, skip to avoid crashing.
  const deviceMem = navigator.deviceMemory;
  if (typeof deviceMem === "number" && deviceMem > 0 && deviceMem < 4) {
    return { ok: false, reason: "low-memory" };
  }
  return { ok: true };
}


function _extOf(filename) {
  return (filename || "").toLowerCase().split(".").pop() || "";
}


/**
 * Decide whether a file is a sensible compression candidate.
 * Pure / synchronous / never throws.
 *
 *   { compress: true }
 *   { compress: false, reason: "feature-off" | "too-small" | "too-large"
 *                            | "wrong-type" | "unsupported" }
 */
export function shouldCompress(file, { force = false } = {}) {
  if (!file) return { compress: false, reason: "no-file" };
  if (!force && !isCompressionFeatureEnabled()) {
    return { compress: false, reason: "feature-off" };
  }
  if (file.size < COMPRESSION_MIN_BYTES) {
    return { compress: false, reason: "too-small" };
  }
  if (file.size > COMPRESSION_MAX_BYTES) {
    return { compress: false, reason: "too-large" };
  }
  const ext = _extOf(file.name);
  if (!COMPRESSIBLE_EXTS.has(ext)) {
    return { compress: false, reason: "wrong-type" };
  }
  const cap = detectCompressionSupport();
  if (!cap.ok) {
    return { compress: false, reason: cap.reason };
  }
  return { compress: true };
}


/**
 * Compress a video file. Resolves with a NEW File (always .mp4).
 * Throws a typed Error on failure — the caller MUST catch and fall
 * back to uploading the original.
 *
 *   const compressed = await compressVideo(file, {
 *     onProgress: ({ pct }) => setOptPct(pct),
 *     signal: abortController.signal,
 *   });
 *
 * Notes:
 *   - The ffmpeg.wasm core is loaded lazily from CDN on first call,
 *     so the bundle stays small for non-compressing users.
 *   - We pin a single transcode preset (H.264 CRF 23, 1080p cap,
 *     30 fps cap, AAC 96k mono). No per-call tuning — keeps the
 *     surface small and predictable.
 *   - If the resulting file isn't smaller than ~85% of the original
 *     we throw `not-smaller` so the caller uploads the original
 *     (paying the compression cost without a meaningful win is bad).
 */
export async function compressVideo(
  file,
  { onProgress, signal } = {},
) {
  if (!file) throw new Error("compressVideo: no file");

  const cap = detectCompressionSupport();
  if (!cap.ok) {
    const e = new Error(`compression unsupported: ${cap.reason}`);
    e.code = "unsupported";
    throw e;
  }

  // Lazy-import — the JS wrapper is small but ffmpeg-core.js + .wasm
  // are big. Keep them out of the main bundle.
  let FFmpeg;
  let fetchFile;
  let toBlobURL;
  try {
    ({ FFmpeg } = await import("@ffmpeg/ffmpeg"));
    ({ fetchFile, toBlobURL } = await import("@ffmpeg/util"));
  } catch (err) {
    const e = new Error(`compression module load failed: ${err?.message || err}`);
    e.code = "module-load";
    throw e;
  }

  const ffmpeg = new FFmpeg();

  // Wire ffmpeg's progress event to our caller.
  if (typeof onProgress === "function") {
    ffmpeg.on("progress", ({ progress }) => {
      // ffmpeg.wasm reports `progress` as 0..1; clamp + scale.
      const pct = Math.max(0, Math.min(100, Math.round((progress || 0) * 100)));
      try { onProgress({ pct }); } catch { /* swallow caller errors */ }
    });
  }

  // Honour an external AbortSignal — ffmpeg has its own .terminate().
  let aborted = false;
  const onAbort = () => { aborted = true; try { ffmpeg.terminate(); } catch {} };
  if (signal) {
    if (signal.aborted) onAbort();
    else signal.addEventListener("abort", onAbort, { once: true });
  }

  try {
    // Load the core (single-threaded, no SharedArrayBuffer required).
    // toBlobURL allows the worker to pull the wasm under same-origin.
    const coreURL = await toBlobURL(FFMPEG_CORE_URL, "text/javascript");
    const wasmURL = await toBlobURL(FFMPEG_WASM_URL, "application/wasm");
    await ffmpeg.load({ coreURL, wasmURL });

    if (aborted) {
      const e = new Error("compression cancelled");
      e.code = "aborted";
      throw e;
    }

    // Write the source file into ffmpeg's virtual FS.
    const inExt = _extOf(file.name) || "mp4";
    const inName = `in.${inExt}`;
    const outName = "out.mp4";
    await ffmpeg.writeFile(inName, await fetchFile(file));

    // Transcode — H.264 CRF 23, 1080p cap (preserve aspect),
    // 30 fps cap, AAC 96k mono. `-movflags +faststart` keeps the
    // moov atom at the front so the assembled file streams cleanly.
    await ffmpeg.exec([
      "-i", inName,
      "-vf", "scale='min(1920,iw)':'-2',fps=30",
      "-c:v", "libx264",
      "-preset", "veryfast",
      "-crf", "23",
      "-pix_fmt", "yuv420p",
      "-c:a", "aac",
      "-b:a", "96k",
      "-ac", "1",
      "-movflags", "+faststart",
      "-y",
      outName,
    ]);

    if (aborted) {
      const e = new Error("compression cancelled");
      e.code = "aborted";
      throw e;
    }

    const outBytes = await ffmpeg.readFile(outName);
    const outBlob = new Blob([outBytes.buffer], { type: "video/mp4" });

    // Don't bother shipping a "compressed" file that's barely smaller —
    // the user gains nothing and we burnt their CPU for no reason.
    if (outBlob.size > file.size * 0.85) {
      const e = new Error("compressed file not meaningfully smaller");
      e.code = "not-smaller";
      throw e;
    }

    // Replace extension with .mp4 (we always emit MP4 H.264).
    const baseName = (file.name || "video").replace(/\.[^.]+$/, "");
    const outFile = new File(
      [outBlob],
      `${baseName}.compressed.mp4`,
      { type: "video/mp4" },
    );

    return outFile;
  } finally {
    if (signal) signal.removeEventListener?.("abort", onAbort);
    try { ffmpeg.terminate(); } catch { /* best-effort */ }
  }
}


/** Friendly file-size formatter for status copy. */
export function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let v = bytes;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u++;
  }
  return `${v.toFixed(v >= 100 || u === 0 ? 0 : 1)} ${units[u]}`;
}

// Exported for tests + diagnostic surfaces.
export const COMPRESSION_THRESHOLDS = {
  minBytes: COMPRESSION_MIN_BYTES,
  maxBytes: COMPRESSION_MAX_BYTES,
  exts: Array.from(COMPRESSIBLE_EXTS),
};
